import React, { useEffect, useState } from "react";
import { Box, Paper, Grid2, Typography, Button } from "@mui/material";
import DynamicTab from "../../extras/DynamicTab.tsx";
import AxiosInstance, { refreshParentChildrenBookings, pollParentSyncStatus } from "../../../utils/Axios.ts";
import Tour from "../TourInterface.ts";
import Parent from "../parentInterface.ts"
import TourCard from "./TourCard.tsx";
import ReportOverlay from "../ReportOverlay.tsx";
import MobileParentInfoCard from "./MobileParentInfoCard.tsx";
import useParentSyncPoll from "../useParentSyncPoll.ts";

interface TourCardListProps {
  pk: string;
}

const TourCardList: React.FC<TourCardListProps> = ({ pk }) => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [parent, setParent] = useState<Parent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [isRefreshingChildren, setIsRefreshingChildren] = useState(false);
  const [lastRefreshClickAt, setLastRefreshClickAt] = useState<number | null>(null);
  const [refreshStatusText, setRefreshStatusText] = useState("");

  const fetchTours = React.useCallback(async () => {
    try {
      const response = await AxiosInstance.get(`/tour/${pk}/`);
      const parentData = await AxiosInstance.get(`/parent/${pk}/`)
      const sortedTours = response.data.sort((a, b) => {
        // Handle null or undefined time_from values
        if (!a.time_from) return 1;
        if (!b.time_from) return -1;

        // First compare by time_from
        const timeComparison = a.time_from.localeCompare(b.time_from);

        // If times are equal, compare by name
        if (timeComparison === 0) {
          // Handle null or undefined name values
          if (!a.name) return 1;
          if (!b.name) return -1;
          const nameComparison = a.name.localeCompare(b.name);

          // If names are also equal, compare by bus_sign
          if (nameComparison === 0) {
        // Handle null or undefined bus_sign values
        if (!a.bus_sign) return 1;
        if (!b.bus_sign) return -1;
        return a.bus_sign.localeCompare(b.bus_sign);
          }

          return nameComparison;
        }

        // If times are different, return that comparison result
        return timeComparison;
      });
      setTours(sortedTours);
      setParent(parentData.data);
    } catch (err) {
      setError("Failed to fetch tours.");
    }
  }
  , [pk]);

  useEffect(() => {
    fetchTours();
  }, [pk, fetchTours]);

  useParentSyncPoll(pk, fetchTours, setParent);

  const refresh = () => {
    fetchTours();
  };

  const refreshCooldownMs = 60 * 1000;
  const refreshDisabled = Boolean(
    isRefreshingChildren ||
      (lastRefreshClickAt && Date.now() - lastRefreshClickAt < refreshCooldownMs)
  );

  const handleRefreshChildren = async () => {
    try {
      setIsRefreshingChildren(true);
      setRefreshStatusText("Refreshing child bookings...");
      setLastRefreshClickAt(Date.now());
      const response = await refreshParentChildrenBookings(pk);
      if (response.status === 202) {
        const result = await pollParentSyncStatus(pk);
        if (result.sync_status === "failed" && result.sync_error) {
          setRefreshStatusText(result.sync_error);
          setError(result.sync_error);
        } else {
          setRefreshStatusText(`Last refresh: ${new Date().toLocaleString()}`);
        }
      } else {
        setRefreshStatusText(`Last refresh: ${new Date().toLocaleString()}`);
      }
      await fetchTours();
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Refresh failed.";
      setRefreshStatusText(message);
      setError(message);
    } finally {
      setIsRefreshingChildren(false);
    }
  };


  if (error) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <DynamicTab title={tours[0]?.parent_name || "Tours"} favicon="/logos/IT logo.png">
    <Box >
      <MobileParentInfoCard
        parent={parent}
        onRefreshChildren={handleRefreshChildren}
        isRefreshingChildren={isRefreshingChildren}
        refreshDisabled={refreshDisabled}
        refreshStatusText={refreshStatusText}
      />
      <Paper style={{ padding: 0, width: "100%" }} >

        <Grid2 container spacing={2} direction="column" alignItems="center" width="100%">
          {tours.map((tour) => (
            <Grid2 width="100%" key={tour.id}>
              <TourCard tour={tour} refresh={refresh} />
            </Grid2>
          ))}
        </Grid2>

        <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        mt={4} // Add margin-top for spacing
        mb={4} // Add margin-bottom for spacing
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => setReportOpen(true)}
        >
          Create Report
        </Button>
      </Box>

      <ReportOverlay
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        parentTourId={pk}
      />
      </Paper>
    </Box>
    </DynamicTab>
  );
};

export default TourCardList;
